/**
 * Central, typed front-end environment configuration for Nizam One.
 *
 * SINGLE SOURCE OF TRUTH. Application code MUST import `env` (and the
 * `isDevelopment` / `isStaging` / `isPreview` / `isProduction` predicates) from
 * here and MUST NOT read `import.meta.env.VITE_SUPABASE_URL`,
 * `import.meta.env.VITE_SUPABASE_ANON_KEY`, or `import.meta.env.VITE_APP_ENV`
 * directly. Only this module and test fixtures may touch those raw vars.
 *
 * SECURITY: this module never logs or throws with the anon key (or any other
 * value) embedded in the message. Errors reference variable *names* and, at
 * most, the (non-secret) project ref — never a key value.
 *
 * The exported `validateEnvConfig()` is a pure function (takes a raw record) so
 * it is unit-tested in isolation; the same ruleset gates production builds via
 * `scripts/check-env-target.mjs`.
 */

export type AppEnv = 'development' | 'staging' | 'preview' | 'production';

export const APP_ENVS: readonly AppEnv[] = ['development', 'staging', 'preview', 'production'];

/**
 * The current Production Supabase project ref. This is a PUBLIC identifier (it
 * appears in the app's Supabase URL and in .mcp.json), NOT a secret. It is used
 * to hard-block a non-production build from pointing at Production, and to
 * require a Production build to target Production.
 * Keep in sync with scripts/check-env-target.mjs.
 */
export const KNOWN_PRODUCTION_REF = 'wzwhvmsfzbtyljsprycl';

/** A Supabase project ref: 20 lowercase alphanumerics (e.g. wzwhvmsfzbtyljsprycl). */
const PROJECT_REF_RE = /^[a-z0-9]{20}$/;

export interface EnvConfig {
  appEnv: AppEnv;
  supabaseUrl: string;
  supabaseProjectRef: string;
  supabaseAnonKey: string;
  /** Optional read-replica for analytics; falls back to the primary client when unset. */
  analyticsUrl?: string;
  analyticsAnonKey?: string;
  /** Absolute app origin used for building links (e.g. invite/callback surfaces). */
  appUrl?: string;
  sentry: { dsn?: string; env?: string; release?: string; dist?: string };
  sales: { companyId?: string; companyName?: string };
  defaultCompanyId?: string;
}

export type RawEnv = Record<string, string | undefined>;

/** Thrown on any environment misconfiguration. Message is always value-free. */
export class EnvConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvConfigError';
  }
}

const isNonEmpty = (v: string | undefined): v is string =>
  typeof v === 'string' && v.length > 0 && v.trim() === v;

/**
 * Parse and validate a Supabase URL, returning its project ref.
 * Enforces: no surrounding/again whitespace, valid absolute URL, https, a
 * `*.supabase.co` hostname, and a well-formed 20-char project ref subdomain.
 * `localhostAllowed` controls whether a localhost URL is permitted (dev only).
 */
export function parseSupabaseUrl(
  raw: string | undefined,
  opts: { localhostAllowed: boolean },
): { url: string; projectRef: string } {
  if (!isNonEmpty(raw)) {
    throw new EnvConfigError('VITE_SUPABASE_URL is required and must be a non-empty, whitespace-free value.');
  }
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    throw new EnvConfigError('VITE_SUPABASE_URL is not a valid URL.');
  }
  if (parsed.protocol !== 'https:') {
    throw new EnvConfigError('VITE_SUPABASE_URL must use https.');
  }
  const host = parsed.hostname.toLowerCase();
  const isLocalhost = host === 'localhost' || host === '127.0.0.1';
  if (isLocalhost) {
    if (!opts.localhostAllowed) {
      throw new EnvConfigError('VITE_SUPABASE_URL must not be localhost in staging or production.');
    }
    return { url: raw, projectRef: 'localhost' };
  }
  if (!host.endsWith('.supabase.co')) {
    throw new EnvConfigError('VITE_SUPABASE_URL must be a *.supabase.co host.');
  }
  const projectRef = host.slice(0, host.length - '.supabase.co'.length);
  if (!PROJECT_REF_RE.test(projectRef)) {
    throw new EnvConfigError('VITE_SUPABASE_URL does not contain a well-formed Supabase project ref.');
  }
  return { url: raw, projectRef };
}

function parseAppEnv(raw: string | undefined): AppEnv {
  if (!isNonEmpty(raw)) {
    throw new EnvConfigError(
      'VITE_APP_ENV is required. Set it to one of: development, staging, preview, production.',
    );
  }
  if (!(APP_ENVS as readonly string[]).includes(raw)) {
    throw new EnvConfigError(
      'VITE_APP_ENV is invalid. Allowed values: development, staging, preview, production.',
    );
  }
  return raw as AppEnv;
}

/**
 * Validate a raw env record and produce a typed, safe config. Throws
 * `EnvConfigError` (value-free message) on any violation.
 *
 * `expectedRef` — when supplied (via a non-secret build-time variable), the
 * resolved project ref MUST equal it. This is how each environment pins its own
 * project. Regardless of `expectedRef`, the following invariants always hold:
 *   - a non-production env may NEVER target the known Production ref;
 *   - a production env MUST target the expected ref (or, absent one, the known
 *     Production ref);
 *   - localhost is only permitted in development.
 */
export function validateEnvConfig(raw: RawEnv, opts: { expectedRef?: string } = {}): EnvConfig {
  const appEnv = parseAppEnv(raw.VITE_APP_ENV);
  const localhostAllowed = appEnv === 'development';
  const { url, projectRef } = parseSupabaseUrl(raw.VITE_SUPABASE_URL, { localhostAllowed });

  const anonKey = raw.VITE_SUPABASE_ANON_KEY;
  if (!isNonEmpty(anonKey)) {
    throw new EnvConfigError(
      'VITE_SUPABASE_ANON_KEY is required and must be a non-empty, whitespace-free value.',
    );
  }

  const expectedRef = isNonEmpty(opts.expectedRef) ? opts.expectedRef : undefined;

  // Invariant: non-production must never point at Production.
  if (appEnv !== 'production' && projectRef === KNOWN_PRODUCTION_REF) {
    throw new EnvConfigError(
      `Environment mismatch: VITE_APP_ENV="${appEnv}" must not target the Production Supabase project.`,
    );
  }

  if (appEnv === 'production') {
    // Production must target the expected ref, or the known Production ref when
    // no expected ref was supplied. It must never target a non-production ref.
    const requiredRef = expectedRef ?? KNOWN_PRODUCTION_REF;
    if (projectRef !== requiredRef) {
      throw new EnvConfigError(
        'Environment mismatch: a production build must target the production Supabase project ref.',
      );
    }
  } else if (expectedRef && projectRef !== expectedRef) {
    // Non-production with a pinned ref must match it exactly.
    throw new EnvConfigError(
      `Environment mismatch: VITE_APP_ENV="${appEnv}" targets a project ref that does not match the expected ref for this environment.`,
    );
  }

  return {
    appEnv,
    supabaseUrl: url,
    supabaseProjectRef: projectRef,
    supabaseAnonKey: anonKey,
    analyticsUrl: isNonEmpty(raw.VITE_SUPABASE_ANALYTICS_URL) ? raw.VITE_SUPABASE_ANALYTICS_URL : undefined,
    analyticsAnonKey: isNonEmpty(raw.VITE_SUPABASE_ANALYTICS_ANON_KEY)
      ? raw.VITE_SUPABASE_ANALYTICS_ANON_KEY
      : undefined,
    appUrl: isNonEmpty(raw.VITE_APP_URL) ? raw.VITE_APP_URL : undefined,
    sentry: {
      dsn: raw.VITE_SENTRY_DSN || undefined,
      env: raw.VITE_SENTRY_ENV || undefined,
      release: raw.VITE_SENTRY_RELEASE || undefined,
      dist: raw.VITE_SENTRY_DIST || undefined,
    },
    sales: {
      companyId: raw.VITE_SALES_COMPANY_ID || undefined,
      companyName: raw.VITE_SALES_COMPANY_NAME || undefined,
    },
    defaultCompanyId: raw.VITE_DEFAULT_COMPANY_ID || undefined,
  };
}

// ── Runtime singleton ─────────────────────────────────────────────────────────
// Under Vitest (MODE==='test' / VITEST) the strict validation is bypassed so the
// unit suite — which runs with CI placeholder vars, not a real project — does not
// crash at import. The strict path runs for every real dev/preview/prod runtime,
// giving defense-in-depth on top of the build-time guard (check-env-target.mjs).
const rawImportMetaEnv = import.meta.env as unknown as RawEnv;
const isVitest =
  rawImportMetaEnv.MODE === 'test' || rawImportMetaEnv.VITEST === 'true' || rawImportMetaEnv.VITEST === '1';

function buildLenientTestConfig(raw: RawEnv): EnvConfig {
  const appEnvRaw = raw.VITE_APP_ENV;
  const appEnv: AppEnv = (APP_ENVS as readonly string[]).includes(appEnvRaw ?? '')
    ? (appEnvRaw as AppEnv)
    : 'development';
  const url = raw.VITE_SUPABASE_URL || 'https://ci-placeholder.supabase.co';
  return {
    appEnv,
    supabaseUrl: url,
    supabaseProjectRef: 'test',
    supabaseAnonKey: raw.VITE_SUPABASE_ANON_KEY || 'test-anon-key',
    analyticsUrl: raw.VITE_SUPABASE_ANALYTICS_URL || undefined,
    analyticsAnonKey: raw.VITE_SUPABASE_ANALYTICS_ANON_KEY || undefined,
    appUrl: raw.VITE_APP_URL || undefined,
    sentry: { dsn: undefined, env: undefined, release: undefined, dist: undefined },
    sales: { companyId: raw.VITE_SALES_COMPANY_ID || undefined, companyName: raw.VITE_SALES_COMPANY_NAME || undefined },
    defaultCompanyId: raw.VITE_DEFAULT_COMPANY_ID || undefined,
  };
}

export const env: EnvConfig = isVitest
  ? buildLenientTestConfig(rawImportMetaEnv)
  : validateEnvConfig(rawImportMetaEnv, { expectedRef: rawImportMetaEnv.VITE_EXPECTED_SUPABASE_PROJECT_REF });

export const isDevelopment = env.appEnv === 'development';
export const isStaging = env.appEnv === 'staging';
export const isPreview = env.appEnv === 'preview';
export const isProduction = env.appEnv === 'production';

// ── Edge Function URL helper ──────────────────────────────────────────────────
// The ONLY sanctioned way to build a Supabase Edge Function URL in the front end.
// It derives the host from the validated, environment-pinned `env.supabaseUrl`, so
// a URL can never point at a project other than the one this environment targets.
// Hardcoded `https://<ref>.supabase.co/functions/v1/…` literals are forbidden by
// scripts/check-hardcoded-supabase-urls.mjs.

/** An Edge Function slug: lowercase alphanumerics in single-hyphen groups (e.g. `api-gateway`). */
const EDGE_FN_NAME_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Pure builder: validate `name` and compose `<supabaseUrl>/functions/v1/<name>`
 * with optional, explicitly-supported query params (URL-encoded). Rejects any
 * name that is a full URL, another hostname, contains a slash, `..`, or a query
 * fragment — so a caller cannot smuggle in an external or alternate-project URL.
 */
export function buildEdgeFunctionUrl(
  supabaseUrl: string,
  name: string,
  opts?: { query?: Record<string, string | number> },
): string {
  if (!EDGE_FN_NAME_RE.test(name)) {
    throw new EnvConfigError(
      `Invalid Edge Function name "${name}". Only lowercase letters, digits, and single hyphens are allowed — ` +
        `no URLs, hostnames, slashes, "..", or query fragments in the name (use the query option for parameters).`,
    );
  }
  let url = `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/${name}`;
  if (opts?.query) {
    const qs = Object.entries(opts.query)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');
    if (qs) url += `?${qs}`;
  }
  return url;
}

/** Build an Edge Function URL for the CURRENT environment's Supabase project. */
export function getEdgeFunctionUrl(
  name: string,
  opts?: { query?: Record<string, string | number> },
): string {
  return buildEdgeFunctionUrl(env.supabaseUrl, name, opts);
}
